// Shared utilities for the composable-analytics recipe builder.

// Client-only stable identity for tree nodes. TreeNode uses `_key` (instead
// of the sibling array index) as the React `key` when rendering a
// composite's children, so reordering (moveChild) moves each node's
// rendered subtree — and any in-progress local component state, e.g. the
// raw text buffered in LeafPredicateForm's CommaListInput — along with it
// instead of letting React rebind a reused component instance to a
// different logical node.
//
// `_key` must never reach the API: the backend RecipeValidator rejects
// unrecognized keys. Always pass a recipe through `stripKeys` before it
// leaves the builder (e.g. in a save payload), and through `ensureKeys`
// when a recipe first enters the builder (recipes loaded from the API, or
// generated on-demand, never have `_key` set).
let keyCounter = 0
const nextKey = () => {
  keyCounter += 1
  return `node-${keyCounter}`
}

export const COMPOSITE_OPS = ['AND', 'OR', 'NOT']

export const isLeaf = (node) => Boolean(node) && typeof node === 'object' && Object.prototype.hasOwnProperty.call(node, 'type')

export const isComposite = (node) => Boolean(node) && typeof node === 'object' && Object.prototype.hasOwnProperty.call(node, 'op') && !isLeaf(node)

export const newLeaf = (type = 'amount_gt') => ({ type, _key: nextKey() })

export const newComposite = (op = 'AND') => {
  if (op === 'NOT') return { op, predicate: newLeaf(), _key: nextKey() }
  return { op, predicates: [newLeaf()], _key: nextKey() }
}

// Recursively assigns a stable `_key` to any node (and its descendants)
// that's missing one. Apply this once, when an existing recipe is seeded
// into the builder (e.g. opening the edit modal, or an AI-generated recipe
// coming back from the server) — nodes created fresh via newLeaf/newComposite
// already carry a `_key` and are left untouched.
export const ensureKeys = (node) => {
  if (!node || typeof node !== 'object') return node
  const keyed = Object.prototype.hasOwnProperty.call(node, '_key') ? node : { ...node, _key: nextKey() }
  if (isComposite(keyed)) {
    if (keyed.op === 'NOT') {
      return keyed.predicate ? { ...keyed, predicate: ensureKeys(keyed.predicate) } : keyed
    }
    return { ...keyed, predicates: (keyed.predicates || []).map(ensureKeys) }
  }
  return keyed
}

// Recursively strips the client-only `_key` before a recipe leaves the
// builder — required at every API boundary (save payloads, etc).
export const stripKeys = (node) => {
  if (!node || typeof node !== 'object') return node
  const { _key, ...rest } = node
  if (isComposite(rest)) {
    if (rest.op === 'NOT') {
      return rest.predicate ? { ...rest, predicate: stripKeys(rest.predicate) } : rest
    }
    return { ...rest, predicates: (rest.predicates || []).map(stripKeys) }
  }
  return rest
}

// Params the section fills at run time (audit-log columns — see
// Composable::SectionAuditColumns) are hidden in the builder, so a stored
// value is one the user can't see or clear, and the payload overwrites it
// anyway. Dropping them at save keeps the stored recipe honest about what
// runs. Only drops what this section actually supplies, so a section with
// no mapping keeps whatever was typed there.
export const stripSuppliedParams = (node, predicates, auditColumns) => {
  if (!node || typeof node !== 'object') return node
  if (isComposite(node)) {
    if (node.op === 'NOT') {
      return node.predicate
        ? { ...node, predicate: stripSuppliedParams(node.predicate, predicates, auditColumns) }
        : node
    }
    return {
      ...node,
      predicates: (node.predicates || []).map((child) => stripSuppliedParams(child, predicates, auditColumns))
    }
  }
  const params = predicates?.[node.type]?.params
  if (!params || !auditColumns) return node

  const next = { ...node }
  params
    .filter((p) => p.source === 'section_audit_columns' && auditColumns[p.name])
    .forEach((p) => delete next[p.name])
  return next
}

// Everything a recipe needs before it leaves the builder for the API.
export const recipeForApi = (node, predicates, auditColumns) =>
  stripKeys(stripSuppliedParams(node, predicates, auditColumns))

export const validateRecipe = (root, predicates, path = 'recipe') => {
  if (!root || typeof root !== 'object') {
    return [{ path, message: 'Node must be an object.' }]
  }
  if (isLeaf(root)) {
    if (!predicates?.[root.type]) {
      return [{ path, message: `Unknown predicate type: ${root.type}` }]
    }
    const schema = predicates[root.type]
    const missing = (schema.params || [])
      .filter((p) => p.required && (root[p.name] === undefined || root[p.name] === '' || root[p.name] === null))
    return missing.map((p) => ({ path: `${path}.${p.name}`, message: `${p.ui_label || p.name} is required.` }))
  }
  if (isComposite(root)) {
    if (!COMPOSITE_OPS.includes(root.op)) {
      return [{ path, message: `Op must be one of ${COMPOSITE_OPS.join(', ')}.` }]
    }
    if (root.op === 'NOT') {
      if (!root.predicate) return [{ path, message: 'NOT requires a predicate child.' }]
      return validateRecipe(root.predicate, predicates, `${path}.predicate`)
    }
    if (!Array.isArray(root.predicates) || root.predicates.length === 0) {
      return [{ path, message: `${root.op} requires at least one child.` }]
    }
    return root.predicates.flatMap((child, idx) =>
      validateRecipe(child, predicates, `${path}.predicates[${idx}]`)
    )
  }
  return [{ path, message: "Node must have either 'type' (leaf) or 'op' (composite)." }]
}

export const defaultRecipe = () => newComposite('AND')
